/* eslint-disable */
import { apiClient } from '../lib/api-client';
import type { User } from '../types/user';
import { RegisterRequestDTO, SocialLoginRequestDTO, UserRole } from '@/types/api/auth';
import { Result } from '@/types/response';
import axios from '../utils/axiosInstance'


export interface LoginPayload {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

export async function login(payload: LoginPayload): Promise<AuthResponse> {
  // Placeholder: swap with real backend path
  return apiClient.post<AuthResponse>('/auth/login', payload);
}

type SocialAuthPayload = {
  user: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
    role_id?: number;
    user_type?: string;
    is_setup?: boolean;
    created_at?: string;
    updated_at?: string;
  };
  token: string;
};

const normaliseRole = (role?: string): UserRole | undefined => {
  if (!role) return undefined;
  const lower = role.toLowerCase();
  if (lower === 'admin' || lower === 'staff' || lower === 'student' || lower === 'preceptor' || lower === 'super_admin') {
    return lower;
  }
  return undefined;
};

export const googleLogin = async (data: SocialLoginRequestDTO): Promise<AuthResponse> => {
  const response = await axios.post<Result<SocialAuthPayload>>(`social-auth/login`, data);
  const payload = response.data.data;
  const rawUser = payload.user;
  const fullName = [rawUser.firstName, rawUser.lastName].filter(Boolean).join(' ').trim();
  const normalisedUser: User = {
    id: rawUser.id,
    email: rawUser.email,
    name: fullName || undefined,
    role: normaliseRole(rawUser.user_type),
    firstName: rawUser.firstName,
    lastName: rawUser.lastName,
    createdAt: rawUser.created_at,
    updatedAt: rawUser.updated_at,
    isSetup: rawUser.is_setup,
    userType: rawUser.user_type,
    roleId: rawUser.role_id,
  };

  return {
    user: normalisedUser,
    token: payload.token,
  };
};

export const register = async (payload: RegisterRequestDTO): Promise<AuthResponse> => {
  return apiClient.post<AuthResponse>('/auth/register', payload);
}

export const onboarding = async (data: PreceptorSetupFormData): Promise<void> => {
  await axios.post<void>(`preceptors/setup`, data);
}

// Student Setup Form Data Interface
export interface StudentSetupFormData {
  // Step 1 - Personal Information
  first_name?: string;
  last_name?: string;
  email?: string;
  // Step 1 - Education
  school: string;
  school_id?: number; // Optional - set when user selects from autocomplete
  program_type: 'FNP' | 'AGACNP' | 'PMHNP' | 'WHNP' | 'PNP' | 'NNP' | 'ENP';
  // Step 1 - Rotation Hours & Requirements
  total_hours_required: number;
  hours_completed: number;
  rotations_completed: number;
  // Step 1 - Eligibility & Status
  background_check_clear: 'passed' | 'in_progress' | 'not_started';
  is_international: boolean;
  visa_status: 'F1' | 'H1B' | 'green_card' | 'citizen' | null;
  // Step 1 - Nursing Credentials
  has_nursing_compact_license?: boolean;
  compact_license_states: string[]; // Array of state codes
  // Step 2 - Seeking Status
  accepts_telehealth: boolean;
  accepts_in_person: boolean;
  is_seeking_rotation?: boolean;
  // Step 3 - Contact
  bio: string;
  email_contacts: Array<{ value: string; label: string }>;
  phone_contacts: Array<{ value: string; label: string }>;
  // Step 2 - Contact Number (minimal)
  phone_country_code?: string;
  phone_number?: string;
  specialty_id?: number | string | null;
  specialty_interest: string;
  // Step 4 - Location
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  preferred_locations: string[]; // Array of state codes
  // Status
  status?: string; // Optional status field for backend compatibility
}

export const studentOnboarding = async (data: StudentSetupFormData): Promise<void> => {
   try {
     // Log what we're sending
     console.log('===== STUDENT ONBOARDING SUBMISSION =====');
     console.log('is_international:', data.is_international);
     console.log('visa_status:', data.visa_status);
     
     // E.164 normalization for phone number
     const normalizePhoneNumber = (phone: string): string => {
       if (!phone) return '';
       // Remove all non-digit characters except leading +
       return phone.replace(/[^\d+]/g, '').replace(/\++/g, '+');
     };
     
     const normalizedPhone = normalizePhoneNumber(data.phone_number || '');
     const fullE164Number = normalizedPhone ? `${data.phone_country_code || '+1'}${normalizedPhone.replace(/^\+/, '')}` : '';
     
     console.log('Phone normalization:', {
       raw: data.phone_number,
       normalized: normalizedPhone,
       country_code: data.phone_country_code,
       e164_full: fullE164Number,
     });
     
     // Sanitize data before sending
     const sanitizedData: any = {
       ...data,
       country: data.country || "US",
       preferred_locations: data.preferred_locations || [],
       compact_license_states: data.compact_license_states || [],
       email_contacts: data.email_contacts || [],
       phone_contacts: data.phone_contacts || [],
       phone_country_code: data.phone_country_code || "+1",
       phone_number: normalizedPhone || undefined,
       phone_e164: fullE164Number || undefined, // Full E.164 formatted number
     };
     
     // Note: Backend will handle visa_status defaulting for international students
     // so we don't need aggressive sanitization here
     
     console.log('Data to send:', {
       is_international: sanitizedData.is_international,
       visa_status: sanitizedData.visa_status,
       country: sanitizedData.country,
     });
     
     await axios.post<void>(`students/setup`, sanitizedData);
     
     console.log('✅ Onboarding successful!');
   } catch (error: any) {
     console.error('❌ Onboarding failed');
     console.error('Status:', error.response?.status);
     console.error('Message:', error.response?.data?.message);
     
     if (error.response?.status === 422 && error.response?.data?.errors) {
       console.error('Validation Errors:');
       Object.entries(error.response.data.errors).forEach(([field, messages]: [string, any]) => {
         console.error(`  ${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`);
       });
     }
     
     throw error;
   }
};

// Certification interface
export interface CertificationData {
  name: string;
  issuer?: string;
  issue_date?: string;
  expiration_date?: string;
  license_number?: string;
  number?: string;
}

// Main form data interface
export interface PreceptorSetupFormData {
  specialty_id?: string | number | null;
  years_experience?: number | null;
  license_state?: string;
  license_states?: string[];
  license_number?: string;
  license_expiration_date?: string;
  accepts_tele?: boolean;
  accepts_in_person?: boolean;
  accepted_program_types?: string[];
  accepted_programs?: { program_id: string; sub_programs: string[] }[];
  availability_notes?: string;
  status?: string;
  // Professional background fields
  graduate_school?: string;
  graduation_date?: string;
  degree_concentration?: string;
  current_position?: string;
  current_position_start_date?: string;
  certifications?: CertificationData[];
  services_provided?: string[];
  // allow future expansion without breaking minimal payload
  [key: string]: unknown;
}

// Contact interface
interface Contact {
  value: string;
  label: string;
}
